import React from 'react';
import {Box, Typography} from '@mui/material';
import {useDashboard} from '../../../context/DashboardContext';
import {getCountryLabel, useGeo} from '../../../utils/geo';
import ScopeSuppressedBanner from '../../DashboardCommon/ScopeSuppressedBanner.comp';

const countryName = (code) => getCountryLabel(code) || code || '';

/**
 * Shared page shell for every National Dashboard section.
 * Renders the page header (title, the dashboard's country), optional tabs, and {children}.
 *
 * One National dashboard per partner × country (6 Oct 2026): the country is chosen in the workspace
 * menu (Insights › National Dashboard › one entry per country) and kept in DashboardContext (per
 * partner in sessionStorage, not in the URL); the header only names it.
 */
const NationalDashboardShell = ({tabs, children}) => {
    useGeo(); // re-render with the API's country names once they load
    // passive: the page itself fetches; the shell only reads the country state
    const {country, availableCountries, dashboardData, loading} = useDashboard({passive: true});
    const needsChoice = !loading && dashboardData === null && !country && (availableCountries || []).length > 1;
    const noneAtAll = !loading && availableCountries && availableCountries.length === 0;
    return (
        <Box>
            {/* Page header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                mb: 2,
                pb: 2.5,
                borderBottom: '1px solid #E5E7EB',
                flexWrap: 'wrap',
                gap: 2,
            }}>
                <Box>
                    <Typography sx={{
                        fontSize: {xs: '20px', md: '26px'},
                        fontWeight: 800,
                        color: '#052F44',
                        lineHeight: 1.2,
                    }}>
                        National Dashboard
                    </Typography>
                    <Typography sx={{fontSize: '14px', color: '#6A7282', mt: 0.5}}>
                        Child online safety readiness, cyber risk and support environment for students
                        {country ? ` · ${countryName(country)}` : ''}
                    </Typography>
                </Box>
            </Box>

            {needsChoice && (
                <Typography sx={{fontSize: '14px', color: '#6A7282', mb: 2}}>
                    Your workspace has schools in several countries. Choose a country under Insights › National Dashboard in the workspace menu.
                </Typography>
            )}
            {noneAtAll && (
                <Typography sx={{fontSize: '14px', color: '#6A7282', mb: 2}}>
                    No National Dashboard yet: none of your schools has completed the assessment in a registered country.
                </Typography>
            )}

            {/* Scores hidden when the partner's population in this country is under the n floor */}
            <ScopeSuppressedBanner/>

            {/* Persistent Tabs Navigation - rendered above content */}
            {tabs}

            {/* Section content rendered unconditionally so child pages/tabs run their own lifecycle */}
            {children}
        </Box>
    );
};

export default NationalDashboardShell;
